/**
 * Determines the next step in the checkout process after login
 *
 * Logic:
 * 1. If order doesn't need shipping → go to pay page
 * 2. If order needs shipping and cart shipping address is for a different
 *    country than cart pricing → go to address page
 * 3. If order needs shipping and cart has shipping address → go to pay page
 * 4. If order needs shipping and cart has no shipping address but user's default
 *    is for a different country than cart → go to address page
 * 5. If order needs shipping, cart has no shipping address, and user has a
 *    default address for the same country → go to pay page
 * 6. Otherwise (needs shipping, no cart address) → go to address page
 */
export function normalizeCheckoutCountryIsoCode(
  countryIsoCode: string | null | undefined
): string {
  const normalized = (countryIsoCode || '').trim().toUpperCase()
  return normalized === 'UK' ? 'GB' : normalized
}

export function checkoutCountryIsoCodesMatch(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  const normalizedA = normalizeCheckoutCountryIsoCode(a)
  const normalizedB = normalizeCheckoutCountryIsoCode(b)
  return Boolean(normalizedA && normalizedB && normalizedA === normalizedB)
}

export function hasCheckoutShippingCountryMismatch(opts: {
  needsShipping: boolean
  cartCountryIsoCode?: string | null
  shippingAddressCountryIsoCode?: string | null
}): boolean {
  if (!opts.needsShipping) return false

  const cartCountry = normalizeCheckoutCountryIsoCode(opts.cartCountryIsoCode)
  const shippingAddressCountry = normalizeCheckoutCountryIsoCode(
    opts.shippingAddressCountryIsoCode
  )

  return Boolean(
    cartCountry &&
    shippingAddressCountry &&
    cartCountry !== shippingAddressCountry
  )
}

export function getNextCheckoutStep(
  needsShipping: boolean,
  hasSavedAddress: boolean,
  opts?: {
    cartHasShippingAddress?: boolean
    cartCountryIsoCode?: string | null
    cartShippingAddressCountryIsoCode?: string | null
    savedAddressCountryIsoCode?: string | null
  }
): string {
  const cartCountry = normalizeCheckoutCountryIsoCode(opts?.cartCountryIsoCode)
  const cartShippingAddressCountry = normalizeCheckoutCountryIsoCode(
    opts?.cartShippingAddressCountryIsoCode
  )
  const savedAddressCountry = normalizeCheckoutCountryIsoCode(
    opts?.savedAddressCountryIsoCode
  )
  const cartHasShippingAddress = opts?.cartHasShippingAddress === true

  // If order doesn't need shipping, go straight to pay
  if (!needsShipping) {
    return '/checkout/pay'
  }

  // A cart priced for one country with a shipping address in another country
  // must be corrected before payment, otherwise the customer can reach the
  // wrong regional payment rails/totals for their actual ship-to address.
  if (
    cartHasShippingAddress &&
    cartCountry &&
    cartShippingAddressCountry &&
    cartCountry !== cartShippingAddressCountry
  ) {
    return '/checkout/address'
  }

  // If cart already has a shipping address attached, go to pay
  if (cartHasShippingAddress) {
    return '/checkout/pay'
  }

  // Cart needs shipping but has no address. If user has default address for a
  // different country than cart, they must visit address page.
  if (
    hasSavedAddress &&
    cartCountry &&
    savedAddressCountry &&
    cartCountry !== savedAddressCountry
  ) {
    return '/checkout/address'
  }

  // User has a usable saved address, so skip address step.
  if (hasSavedAddress) {
    return '/checkout/pay'
  }

  // Order needs shipping, cart has no address and no saved address.
  return '/checkout/address'
}
